/**
 * Metered free-tier client helpers.
 *
 * The backend (branch feat/free-tier-daily-cap in aswb-backend) enforces the
 * daily cap in the question-serving callables. On a successful call it returns a
 * `dailyFree: { remaining, limit }` allowance; when the cap is exhausted it
 * throws a Firebase functions error with code `resource-exhausted` and details
 * `{ remaining: 0, limit, resetsAtUtc }`.
 *
 * This module models both signals so the UI can (a) show an allowance chip and
 * (b) render an in-flow paywall instead of the generic error card. Everything
 * here is inert unless the backend actually emits these signals, so it is safe
 * to ship with the client feature flag OFF.
 *
 * @module utils/freeTier
 */

/** Daily free-question allowance carried by successful question-serving responses. */
export interface DailyFreeAllowance {
  /** Questions remaining for the user today. */
  remaining: number;
  /** The daily cap (e.g. 5). */
  limit: number;
}

/**
 * Firebase surfaces callable error codes prefixed with `functions/`. The backend
 * throws the bare gRPC code `resource-exhausted`.
 */
const RESOURCE_EXHAUSTED_CODES = new Set([
  'resource-exhausted',
  'functions/resource-exhausted',
]);

/** Message/details marker the backend attaches so we can disambiguate cause. */
export const DAILY_LIMIT_MARKER = 'daily-limit-reached';

/**
 * Typed error thrown by the API layer when the daily free cap is hit. Carries the
 * details the paywall needs (limit + when the allowance resets). `limit` is
 * required and always a real, finite cap: construction only happens after
 * {@link parseDailyLimitError} has validated the backend's details, so the UI
 * never needs a hard-coded fallback number.
 */
export class DailyLimitError extends Error {
  readonly remaining: number;
  readonly limit: number;
  readonly resetsAtUtc?: string;

  constructor(details: { remaining?: number; limit: number; resetsAtUtc?: string }) {
    super(DAILY_LIMIT_MARKER);
    this.name = 'DailyLimitError';
    this.remaining = details.remaining ?? 0;
    this.limit = details.limit;
    this.resetsAtUtc = details.resetsAtUtc;
    // Restore prototype chain for `instanceof` across transpile targets.
    Object.setPrototypeOf(this, DailyLimitError.prototype);
  }
}

/** Type guard: is this a {@link DailyLimitError}? */
export const isDailyLimitError = (error: unknown): error is DailyLimitError =>
  error instanceof DailyLimitError;

/**
 * Detect a raw Firebase callable error that represents a hit daily cap.
 *
 * Classification is deliberately strict — BOTH must hold:
 * 1. The error carries the backend's allowance details: a finite, positive
 *    `details.limit`. Unrelated `resource-exhausted` errors (Firebase quota,
 *    rate limits, App Check throttling) never carry this shape, so they fall
 *    through to normal error handling instead of masquerading as the paywall
 *    (and inflating the `free_cap_hit` conversion metric).
 * 2. The error is corroborated by the `daily-limit-reached` marker (in the
 *    message or details) or the `resource-exhausted` code.
 *
 * Returns the parsed details (with a guaranteed real `limit`), or `null` if it
 * is not a daily-limit error.
 */
export const parseDailyLimitError = (
  error: unknown,
): { remaining: number; limit: number; resetsAtUtc?: string } | null => {
  if (isDailyLimitError(error)) {
    return { remaining: error.remaining, limit: error.limit, resetsAtUtc: error.resetsAtUtc };
  }

  const err = error as { code?: string; message?: string; details?: unknown } | null;
  if (!err) return null;

  const code = typeof err.code === 'string' ? err.code : '';
  const message = typeof err.message === 'string' ? err.message : '';
  const details = (err.details ?? {}) as {
    remaining?: number;
    limit?: number;
    resetsAtUtc?: string;
    reason?: string;
    message?: string;
  };

  const hasValidLimit =
    typeof details.limit === 'number' && Number.isFinite(details.limit) && details.limit > 0;
  if (!hasValidLimit) return null;

  const hasMarker =
    message.includes(DAILY_LIMIT_MARKER) ||
    details.reason === DAILY_LIMIT_MARKER ||
    details.message === DAILY_LIMIT_MARKER;

  if (!hasMarker && !RESOURCE_EXHAUSTED_CODES.has(code)) return null;

  return {
    remaining: typeof details.remaining === 'number' ? details.remaining : 0,
    limit: details.limit as number,
    resetsAtUtc: typeof details.resetsAtUtc === 'string' ? details.resetsAtUtc : undefined,
  };
};
